/**
 * ClientPortalEngine.jsx — IMMUTABLE Guest Portal logic (Iter-314).
 *
 * Owns every piece of business logic shared by the four Guest-facing portal
 * tabs (Book · My Visit · Concierge · Profile). Centralises:
 *
 *   1. **Privacy Gate (Hardcoded)** — `internal_notes` and every other
 *      staff-only key MUST NEVER appear in a data object rendered to the
 *      guest. `sanitizeVisitHistoryEntry` deletes forbidden keys defensively
 *      even if a future backend regression accidentally projects them.
 *      Belt-and-braces on top of the server-side projection in
 *      `/api/customers/me/medical-history`.
 *
 *   2. **Visit Timeline** — grouping, sorting, and per-card derivation for
 *      the 1:1 visit timeline (Date · Branch · Care Team · Session notes ·
 *      Photos · Invoice metadata). Package redemptions and linked visits
 *      each render as their own card (one line per booked appointment).
 *
 *   3. **Photo Thumbnails + Full-Screen Preview** — the engine re-exports
 *      `AttachmentThumbnails` and `MedicalPhotoLightbox` so every portal
 *      surface loads guest photos through the same secure path (lazy-load,
 *      `loading="lazy"`, no naked <img> URLs elsewhere).
 *
 *   4. **4-Tab Navigation State** — `PORTAL_TABS` (Book · My Visit ·
 *      Concierge · Profile) + `isPortalTabActive(pathname, to)` are the
 *      single source of truth for the mobile user journey. Reverting to
 *      a hamburger sheet or inline TAB definitions in `CustomerNav.jsx`
 *      is a P0 regression.
 *
 * MANDATORY CLAUSES (Iter-314):
 *   - **Global Scope**: applies to all 8 branches (Ubud, Canggu, Seminyak
 *     and any future openings). One engine, one behaviour, forever.
 *   - **Data Persistence**: engine is pure — it reads what the server
 *     returns and never writes back. Data persistence stays with the
 *     backend endpoints (`/appointments/mine`, `/customers/me/*`).
 *   - **Live State Refresh**: consumers MUST NOT cache derived state
 *     across renders. Every consumer wraps engine calls in `useMemo` so
 *     fresh server payloads re-derive on every fetch / event tick.
 *
 * IMMUTABILITY: Do NOT edit this file during UI, styling, ESLint autofix,
 * Prettier reformat, or any "cleanup" task. Genuine bug fixes require a
 * dedicated task and a matching update to SYSTEM_RULES.md.
 *
 * Purity contract (JS helpers): no React state, no I/O, no DOM. Component
 * re-exports below are thin passthroughs to the existing lazy-loaded
 * thumbnail + lightbox components.
 */

import React from "react";
import { CalendarPlus, ClipboardList, Sparkles, UserCircle2 } from "lucide-react";
import AttachmentThumbnailsComponent from "@/components/medical/AttachmentThumbnails";
import MedicalPhotoLightboxComponent from "@/components/medical/MedicalPhotoLightbox";

// =============================================================================
// 1. PRIVACY GATE (Hardcoded)
// =============================================================================

/**
 * Keys that MUST NEVER appear in an object rendered to the guest.
 * Belt-and-braces defence on top of the server-side projection.
 * Extending this list is the ONLY approved way to add new forbidden fields.
 */
export const PORTAL_FORBIDDEN_HISTORY_KEYS = Object.freeze([
  "internal_notes",
  "internal_note",
  "staff_notes",
  "admin_notes",
  "admin_note",
  "private_notes",
  "clinician_only_notes",
]);

/**
 * Whitelist of keys the guest is ALLOWED to see on a visit-history entry.
 * Anything outside this list is stripped by `sanitizeVisitHistoryEntry`.
 */
export const PORTAL_ALLOWED_HISTORY_KEYS = Object.freeze([
  "appointment_id",
  "service_name",
  "package_name",
  "start_iso",
  "branch_id",
  "branch_name",
  "team",
  "author_name",
  "author_position",
  "session_notes",
  "notes",
  "treatments_done",
  "follow_up",
  "attachments",
  "order_id",
  "order_number",
  "order_total",
  "order_closed_at",
]);

/**
 * Deep-sanitise a single visit-history entry so `internal_notes` and every
 * other staff-only field is provably absent from the object that reaches
 * the guest UI. Whitelist-based — safer than blacklist-only.
 */
export function sanitizeVisitHistoryEntry(entry) {
  if (!entry || typeof entry !== "object") return entry;
  const clean = {};
  for (const key of PORTAL_ALLOWED_HISTORY_KEYS) {
    if (key in entry) clean[key] = entry[key];
  }
  // Defence-in-depth: even if the whitelist is accidentally extended in
  // future, the blocklist wipes any forbidden key that slipped through.
  for (const k of PORTAL_FORBIDDEN_HISTORY_KEYS) {
    if (k in clean) delete clean[k];
  }
  return clean;
}

/**
 * Sanitise the full `/customers/me/medical-history` payload the guest fetches.
 * Returns a fresh object safe to hand to the UI.
 */
export function sanitizeVisitHistoryPayload(payload) {
  const src = payload || {};
  const days = Array.isArray(src.history_by_date) ? src.history_by_date : [];
  return {
    ...src,
    history_by_date: days.map((day) => ({
      ...day,
      entries: Array.isArray(day.entries) ? day.entries.map(sanitizeVisitHistoryEntry) : [],
    })),
    count: Number(src.count || 0),
  };
}

// =============================================================================
// 2. VISIT TIMELINE — grouping, sorting, per-card derivation
// =============================================================================

const _toTime = (iso) => {
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : 0;
};

export const isFutureAppt = (a) => _toTime(a?.start_iso) > Date.now();
export const isDoneAppt = (a) => a?.status === "cancelled" || a?.status === "no_show";

/** Future & non-cancelled appointments, soonest first. */
export function deriveUpcomingAppointments(appts) {
  return [...(appts || [])]
    .filter((a) => isFutureAppt(a) && !isDoneAppt(a))
    .sort((x, y) => _toTime(x.start_iso) - _toTime(y.start_iso));
}

/** Past-dated or cancelled/no-show appointments, newest first. */
export function derivePastAppointments(appts) {
  return [...(appts || [])]
    .filter((a) => !isFutureAppt(a) || isDoneAppt(a))
    .sort((x, y) => _toTime(y.start_iso) - _toTime(x.start_iso));
}

/**
 * Build a { appointment_id → sanitised history entry } lookup from a
 * medical-history payload. Every entry passes through the privacy gate
 * before it reaches the callsite.
 */
export function buildHistoryByApptIndex(historyPayload) {
  const map = {};
  const days = (historyPayload && historyPayload.history_by_date) || [];
  for (const day of days) {
    for (const raw of day.entries || []) {
      const e = sanitizeVisitHistoryEntry(raw);
      if (e && e.appointment_id) map[e.appointment_id] = e;
    }
  }
  return map;
}

/** Format a visit's start_iso in Bali time — "26 Feb 2026". */
export function formatVisitDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

/** Format a visit's start_iso in Bali time — "14:30" (24h). */
export function formatVisitTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

/** Format Bali-time in AM/PM — "2:30 PM". */
export function formatVisitTime12h(iso) {
  try {
    return new Date(iso).toLocaleTimeString("en-US", {
      timeZone: "Asia/Makassar",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "";
  }
}

/**
 * Derive every field the visit card renders — Date · Branch · Care Team ·
 * Session notes · Photos · Invoice metadata. The engine centralises the
 * "is there anything clinical to show?" decision so consumers just render
 * the returned booleans.
 *
 * The history entry is re-sanitised here as a final belt-and-braces guard.
 */
export function deriveVisitCardDetail(appt, historyEntry, branchesById) {
  const h = sanitizeVisitHistoryEntry(historyEntry || null);
  const teamNames = ((h && h.team) || [])
    .filter((m) => m && m.name)
    .map((m) => m.name)
    .join(", ");
  const hasNotes = !!(h && (h.session_notes || h.notes) && String(h.session_notes || h.notes).trim().length > 0);
  const hasTreatments = !!(h && h.treatments_done && String(h.treatments_done).trim().length > 0);
  const hasFollowUp = !!(h && h.follow_up && String(h.follow_up).trim().length > 0);
  const hasAttachments = !!(h && Array.isArray(h.attachments) && h.attachments.length > 0);
  const branchName = (branchesById && branchesById[appt?.branch_id]?.name) || (h && h.branch_name) || "";
  return {
    entry: h,
    teamNames,
    hasNotes,
    hasTreatments,
    hasFollowUp,
    hasAttachments,
    branchName,
    date: formatVisitDate(appt?.start_iso),
    time: formatVisitTime(appt?.start_iso),
  };
}

// =============================================================================
// 3. PHOTO THUMBNAILS + FULL-SCREEN PREVIEW
//    Re-exported so every portal surface imports the guest-safe path from
//    the engine, never a naked <img> URL.
// =============================================================================

export const AttachmentThumbnails = AttachmentThumbnailsComponent;
export const MedicalPhotoLightbox = MedicalPhotoLightboxComponent;

// =============================================================================
// 4. 4-TAB NAVIGATION STATE (Book · My Visit · Concierge · Profile)
// =============================================================================

/**
 * Single source of truth for the guest-facing 4-tab navigation.
 * Reverting to a hamburger sheet or defining tabs inline anywhere else
 * is a P0 regression.
 */
export const PORTAL_TABS = Object.freeze([
  { to: "/portal/book", label: "Book", testid: "nav-book", icon: CalendarPlus },
  { to: "/portal/appointments", label: "My Visit", testid: "nav-appointments", icon: ClipboardList },
  { to: "/portal/concierge", label: "Concierge", testid: "nav-concierge", icon: Sparkles },
  { to: "/portal/profile", label: "Profile", testid: "nav-profile", icon: UserCircle2 },
]);

/** `true` iff the current pathname is (or is within) the given tab route. */
export function isPortalTabActive(pathname, to) {
  if (!pathname || !to) return false;
  return pathname === to || pathname.startsWith(to + "/");
}

// =============================================================================
// 5. VISIT TIMELINE — Day + Package Grouping (Iter-345)
//    Guest Portal "My Visit / My Journey" MUST group services sharing the same
//    `package_id` into ONE bundle rendered under a bold Package Name header,
//    with the individual treatments nested underneath. This is a UI-only
//    grouping — the underlying appointments keep their own `package_id` in
//    the database (no shredding). Package name resolution order:
//        1. `packagesById[package_id].name` (brand catalog — authoritative)
//        2. `historyByAppt[first_appt_id].package_name` (server-projected)
//        3. Literal fallback "Package".
//    Applies globally to all 8 branches (Ubud, Canggu, Seminyak, Legian,
//    Jimbaran, Bahrain, and any future openings).
// =============================================================================

/** Bali-local YYYY-MM-DD key so appointments straddling UTC midnight don't split. */
export function baliDateKey(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-CA", { timeZone: "Asia/Makassar" });
  } catch { return ""; }
}

/** Human-readable Bali date — "Sat, 19 Sep 2026". */
export function baliDateDisplay(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      timeZone: "Asia/Makassar",
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch { return ""; }
}

/** Bali 24-hour HH:MM. */
export function baliTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString("en-GB", {
      timeZone: "Asia/Makassar",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch { return ""; }
}

/**
 * Group visits by Bali calendar day + branch. Inside each day, services
 * sharing the same `package_id` fold into ONE `kind: "package"` bucket with
 * a bold Package Name header; everything else stays a `kind: "solo"` row.
 *
 * Package name resolution is data-integrity safe:
 *   • First choice: `packagesById[package_id].name` (brand catalog).
 *   • Second choice: `historyByAppt[first_appt_id].package_name`
 *     (server-projected on medical-history entries).
 *   • Fallback: literal "Package" (never hides the fact that these lines
 *     belong to a bundle).
 *
 * The `package_id` on every appointment is preserved — this helper only
 * READS the field; the DB is never mutated.
 *
 * @param {Array} appts — appointments (already filtered upcoming or past).
 * @param {Object} historyByAppt — map appt_id → sanitised history entry.
 * @param {Object} branchesById — map branch_id → branch doc.
 * @param {Object} [packagesById] — map package_id → catalog package doc.
 * @returns {Array} ordered day cards, each with a `buckets` array.
 */
// =============================================================================
// 6. UPCOMING CALENDAR — Date-Column Grid (Iter-353)
//    Guest Portal "My Visit" upcoming section renders a vertical calendar
//    grid where each column = one appointment date and the vertical axis
//    is a 06:00 → 23:30 time scale in 30-minute slots. Every tile carries
//    the service or package name, the start–end time, care-team name and
//    branch. Tiles are positioned by their Bali-local minute-of-day so
//    the surface matches the Staff Calendar convention.
//
//    IMMUTABILITY: this section is part of the engine and follows the
//    same "do NOT edit during UI/styling tasks" contract as the rest of
//    the file. Adding new fields to the tile record requires a matching
//    consumer update AND a rule update in SYSTEM_RULES.md.
//
//    Applies globally to all 8 branches (Ubud, Canggu, Seminyak, Legian,
//    Jimbaran, Bahrain, and any future openings). Pure JS — no state,
//    no I/O, no DOM.
// =============================================================================

/** Vertical axis lower bound (minutes-of-day). 06:00 Bali. */
export const PORTAL_CAL_START_MIN = 6 * 60;
/** Vertical axis upper bound (minutes-of-day, inclusive). 23:30 Bali. */
export const PORTAL_CAL_END_MIN = 23 * 60 + 30;
/** Slot granularity — 30 minutes. */
export const PORTAL_CAL_SLOT_MIN = 30;
/** Max columns visible per page before Next/Prev pagination kicks in. */
export const PORTAL_CAL_MAX_COLS = 6;

/**
 * "Sat, 26/Feb/2026" — the exact spec header for every date column.
 * Bali-timezone locked so a browser in a different timezone still shows
 * the day the guest booked in Bali.
 */
export function formatPortalCalendarHeader(iso) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Makassar",
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).formatToParts(d);
    const get = (t) => parts.find((p) => p.type === t)?.value || "";
    return `${get("weekday")}, ${get("day")}/${get("month")}/${get("year")}`;
  } catch { return ""; }
}

/** Bali-local minute-of-day (0..1439) from an ISO string. */
export function baliMinuteOfDay(iso) {
  try {
    const hm = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Makassar",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(new Date(iso));
    const [h, m] = hm.split(":").map((n) => parseInt(n, 10));
    if (!Number.isFinite(h) || !Number.isFinite(m)) return 0;
    return (h * 60) + m;
  } catch { return 0; }
}

/** Slot ticks from start to end inclusive — used by the vertical axis. */
export function buildPortalCalendarSlots(
  startMin = PORTAL_CAL_START_MIN,
  endMin = PORTAL_CAL_END_MIN,
  slotMin = PORTAL_CAL_SLOT_MIN,
) {
  const out = [];
  for (let t = startMin; t <= endMin; t += slotMin) out.push(t);
  return out;
}

/** "6:00 AM" / "1:30 PM" — 12h AM/PM label for the vertical time axis. */
export function formatPortalSlotLabel(minute) {
  const h24 = Math.floor(minute / 60);
  const m = minute % 60;
  const suffix = h24 >= 12 ? "PM" : "AM";
  const h12 = ((h24 + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${suffix}`;
}

/** "HH:MM" 24-hour end-time label given the appointment's end-of-day minute. */
function _endBaliLabel(endMinOfDay) {
  const total = ((endMinOfDay % 1440) + 1440) % 1440;
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

/**
 * Derive the care-team display string from either the medical-history
 * projection (staff-authenticated team list) or the appointment's
 * denormalised `staff_name`. Prefers the multi-member team list so a
 * multi-staff service shows every practitioner.
 */
function _teamNamesForAppt(appt, historyEntry) {
  const h = historyEntry;
  if (h && Array.isArray(h.team) && h.team.length) {
    return h.team
      .filter((m) => m && m.name)
      .map((m) => m.name)
      .join(", ");
  }
  return (appt && appt.staff_name) || "";
}

/**
 * Group appointments into date columns for the upcoming-calendar grid.
 *
 * One column per Bali-local date, sorted ascending. Each column carries
 * an ordered list of tiles positioned by their minute-of-day. Package
 * children keep their own tile; the tile title falls back to the
 * package name per Iter-345 resolution order (catalog → history →
 * literal "Package"). Solo appointments render with their `service_name`.
 *
 * The `package_id` on every appointment is preserved — this helper only
 * READS the field (belt-and-braces on top of the backend contract).
 *
 * @param {Array} appts — upcoming appointments (already filtered).
 * @param {Object} branchesById — { branch_id → branch doc }.
 * @param {Object} packagesById — { package_id → catalog package doc }.
 * @param {Object} historyByAppt — { appt_id → sanitised history entry }.
 * @returns {Array} sorted array of `{date_key, header, first_iso, tiles[]}`.
 */
export function buildPortalCalendarColumns(appts, branchesById, packagesById, historyByAppt) {
  const _appts = Array.isArray(appts) ? appts : [];
  const _branches = branchesById || {};
  const _packages = packagesById || {};
  const _hist = historyByAppt || {};

  const byDate = new Map();
  for (const a of _appts) {
    const dk = baliDateKey(a?.start_iso);
    if (!dk) continue;
    if (!byDate.has(dk)) {
      byDate.set(dk, {
        date_key: dk,
        header: formatPortalCalendarHeader(a.start_iso),
        first_iso: a.start_iso,
        tiles: [],
      });
    }
    const col = byDate.get(dk);
    if ((a.start_iso || "") < (col.first_iso || "")) col.first_iso = a.start_iso;

    const pkgId = a.package_id || "";
    const catalogName = pkgId ? _packages[pkgId]?.name : "";
    const historyPkgName = pkgId ? _hist[a.id]?.package_name : "";
    const packageName = pkgId ? (catalogName || historyPkgName || "Package") : "";

    const durationMin = Number(a.duration_min) || 30;
    const startMin = baliMinuteOfDay(a.start_iso);
    const endMin = startMin + durationMin;
    const startLabel = baliTime(a.start_iso);
    const endLabel = _endBaliLabel(endMin);
    const teamNames = _teamNamesForAppt(a, _hist[a.id]);
    const branchName = _branches[a.branch_id]?.name || _hist[a.id]?.branch_name || "";

    col.tiles.push({
      id: a.id,
      appointment_id: a.id,
      title: packageName || a.service_name || "Appointment",
      service_name: a.service_name || "",
      package_id: pkgId || null,
      package_name: packageName || null,
      start_iso: a.start_iso,
      end_iso: a.end_iso || null,
      start_min: startMin,
      end_min: endMin,
      duration_min: durationMin,
      time_range: `${startLabel}–${endLabel}`,
      start_label: startLabel,
      end_label: endLabel,
      team_names: teamNames,
      branch_name: branchName,
      branch_id: a.branch_id || null,
      status: a.status || "booked",
      service_id: a.service_id || null,
      raw: a,
    });
  }

  const cols = Array.from(byDate.values()).sort(
    (x, y) => (x.date_key || "").localeCompare(y.date_key || ""),
  );
  for (const c of cols) c.tiles.sort((x, y) => x.start_min - y.start_min);
  return cols;
}

/**
 * Position a tile inside the calendar body. Returns `{top, height}` in
 * pixels. Clamps to the visible window so an out-of-range appointment
 * (e.g. a legacy 04:00 record) still paints along the top edge instead
 * of overflowing.
 */
export function computePortalTileGeometry(
  startMin,
  durationMin,
  slotPx,
  gridStartMin = PORTAL_CAL_START_MIN,
  gridEndMin = PORTAL_CAL_END_MIN,
  slotMin = PORTAL_CAL_SLOT_MIN,
) {
  const s = Math.max(gridStartMin, Math.min(startMin, gridEndMin));
  const e = Math.max(gridStartMin, Math.min(startMin + (durationMin || 0), gridEndMin));
  const top = ((s - gridStartMin) / slotMin) * slotPx;
  const height = Math.max(((e - s) / slotMin) * slotPx, slotPx * 0.6);
  return { top, height };
}

/**
 * Slice a column list into visible pages of `pageSize` columns and expose
 * has_prev / has_next flags so the surface can wire Next/Prev buttons.
 */
export function pagePortalCalendarColumns(columns, page = 0, pageSize = PORTAL_CAL_MAX_COLS) {
  const size = Math.max(1, Number(pageSize) || PORTAL_CAL_MAX_COLS);
  const list = Array.isArray(columns) ? columns : [];
  const totalPages = Math.max(1, Math.ceil(list.length / size));
  const clamped = Math.max(0, Math.min(Number(page) || 0, totalPages - 1));
  const start = clamped * size;
  return {
    visible: list.slice(start, start + size),
    page: clamped,
    total_pages: totalPages,
    has_prev: clamped > 0,
    has_next: clamped < totalPages - 1,
    page_size: size,
  };
}

export function buildDayVisitCards(appts, historyByAppt, branchesById, packagesById) {
  const _appts = Array.isArray(appts) ? appts : [];
  const _hist = historyByAppt || {};
  const _branches = branchesById || {};
  const _packages = packagesById || {};

  // Group by "{dateKey}::{branchId}" — a same-day two-branch visit gets
  // two cards, mirroring the operator calendar convention.
  const dayMap = new Map();
  for (const a of _appts) {
    const dk = baliDateKey(a.start_iso);
    const bid = a.branch_id || "";
    const key = `${dk}::${bid}`;
    if (!dayMap.has(key)) {
      dayMap.set(key, {
        key,
        date_key: dk,
        date_display: baliDateDisplay(a.start_iso),
        first_iso: a.start_iso,
        branch_id: bid,
        branch_name: _branches[bid]?.name || _hist[a.id]?.branch_name || "",
        appts: [],
      });
    }
    const day = dayMap.get(key);
    day.appts.push(a);
    if (a.start_iso < day.first_iso) day.first_iso = a.start_iso;
  }

  const days = Array.from(dayMap.values());
  for (const d of days) {
    d.appts.sort((x, y) => (x.start_iso || "").localeCompare(y.start_iso || ""));
    const bucketMap = new Map();
    const buckets = [];
    for (const a of d.appts) {
      const pkgId = a.package_id || "";
      if (pkgId) {
        const bk = `pkg::${pkgId}`;
        if (!bucketMap.has(bk)) {
          const catalog = _packages[pkgId];
          const historyName = _hist[a.id]?.package_name;
          const packageName = (catalog && catalog.name) || historyName || "Package";
          const b = {
            kind: "package",
            key: bk,
            package_id: pkgId,
            package_name: packageName,
            appts: [],
          };
          bucketMap.set(bk, b);
          buckets.push(b);
        } else {
          // Later sibling might carry a history-projected package_name
          // when the earlier one didn't — upgrade the header only if we
          // still hold the "Package" fallback (never override catalog).
          const b = bucketMap.get(bk);
          if (b.package_name === "Package") {
            const hn = _hist[a.id]?.package_name;
            if (hn) b.package_name = hn;
          }
        }
        bucketMap.get(bk).appts.push(a);
      } else {
        buckets.push({ kind: "solo", key: `solo::${a.id}`, appts: [a] });
      }
    }
    d.buckets = buckets;
  }
  return days;
}
